import { BRIDGE_PATTERNS } from './bridge-patterns'
import { KALQY_WORLD } from './kalqy-world'
import { LETTER_LAGOON } from './letter-lagoon'
import { SKY_QUEST } from './sky-quest'
import { TUROS_WORLD } from './turos-world'
import type { GameContent, GameId, MotorAction } from './types'

/**
 * The games registry — the one list of all five.
 *
 * 🔴 FIVE TILES, NOT SIX (ADR-031, OD-10), AND THE TYPE SAYS SO. The tuple
 * length is fixed, so a sixth entry does not compile — a stronger statement than
 * a comment on whichever UI file happens to render them, and it is what B2 maps
 * over rather than assembling its own list.
 *
 * Colour is deliberately absent: it is covered as an *attribute* inside Bridge
 * Patterns L3 (sort by one attribute), which is OD-10's recorded default.
 */
export const GAMES: readonly [GameContent, GameContent, GameContent, GameContent, GameContent] = [
  SKY_QUEST,
  LETTER_LAGOON,
  TUROS_WORLD,
  KALQY_WORLD,
  BRIDGE_PATTERNS,
]

/**
 * 🔴 THE DOCUMENTED DEFAULT ORDER — AND IT HAS FOUR GAMES IN IT.
 *
 * games-spec.md §2: *"Default order: Sky Quest → Turo's World → Letter Lagoon →
 * Bridge Patterns."* **Kalqy World is missing.** The four alternate perfectly
 * (high · low · high · low), which is what you would get either by writing the
 * order before the medium game existed, or by leaving out the one that does not
 * fit the pattern.
 *
 * Recorded as **OD-46** rather than guessed at: the doc's four are kept exactly
 * as written, and Kalqy World stays reachable from the library — so nothing is
 * silently dropped *and* nothing is silently added.
 *
 * ⚠️ Until that is answered, **a child playing only default sessions never
 * meets Social Awareness**, which is a curriculum hole rather than a scheduling
 * detail.
 */
export const DEFAULT_ORDER: readonly GameId[] = [
  'sky-quest',
  'turos-world',
  'letter-lagoon',
  'bridge-patterns',
]

export function gameById(id: GameId): GameContent | undefined {
  return GAMES.find((g) => g.id === id)
}

/**
 * What B2 shows on a tile — *"jump / reach"*, *"step / hop"*.
 *
 * 🔴 DERIVED, NEVER A NEW FIELD. A tile that carried its own copy could
 * disagree with the content, and the disagreement would be invisible until a
 * child picked a game expecting to jump.
 *
 * ⚠️ Nulls are skipped rather than reported. Sky Quest L1's `motorAction` is
 * deliberately `null` — NCF-FS puts subitizing before symbols, so the first
 * level a 3-year-old meets is point-only — and reporting "no control" for a game
 * whose other four levels ask for jumps would be wrong in the one place a
 * teacher looks to choose a game.
 */
export function controlTypes(game: GameContent): MotorAction['kind'][] {
  const kinds = game.levels.map((l) => l.motorAction?.kind).filter((k): k is MotorAction['kind'] => !!k)
  return [...new Set(kinds)]
}

/**
 * Does this game have a gross-motor core — games-spec §1's three-of-five rule?
 *
 * 🔴 IT READS BOTH FIELDS, AND THAT IS THE WHOLE FIX. Judging on `motorAction`
 * alone counted 2 of 5 while looking straight at the most physical game in the
 * lineup: Kalqy World answers by POINTING, and does its running, jumping and
 * squatting between the gates where no single answer-verb field could see it.
 *
 * ⚠️ NOT DERIVED FROM `exertion`. That describes how much a child moves and is
 * explicitly not a difficulty rating; a game could be exhausting and entirely
 * seated. This asks which movements, which is a different question.
 */
const GROSS: ReadonlySet<string> = new Set(["jump", "step", "gesture", "duck"])

export function isGrossMotor(game: GameContent): boolean {
  return (
    controlTypes(game).some((k) => GROSS.has(k)) ||
    (game.movement ?? []).some((m) => GROSS.has(m))
  )
}
